import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchMarketFeed, isSaudiMarketOpen, riyadhTime } from './marketFeed.ts';

interface MarketStock {
    name: string;
    code: string;
    change: string;
    changePct: string;
    price: string;
    liquidity: string;
    volume?: string;
    isPositive?: boolean;
}

const parseNum = (val: any): number => {
    if (!val || val === '-') return 0;
    const str = String(val).replace(/%/g, '').replace(/,/g, '').trim();
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
};

const Dchip = () => (
    <span
        className="inline-flex items-center justify-center w-4 h-4 rounded bg-amber-400/15 border border-amber-400/40 text-amber-300 text-[10px] font-extrabold"
        title="بيانات الزوار مؤجلة حتى 15 دقيقة"
    >D</span>
);

const TopTenMarketStats: React.FC = () => {
    const { t } = useTranslation();
    const [topGainers, setTopGainers] = useState<MarketStock[]>([]);
    const [topLiquidity, setTopLiquidity] = useState<MarketStock[]>([]);
    const [stamp, setStamp] = useState('');
    const [mode, setMode] = useState<'delayed' | 'close' | ''>('');

    const fetchMarketData = async () => {
        try {
            const { rows: stocks, meta } = await fetchMarketFeed();

            const formatStock = (stock: any): MarketStock => {
                const rawPct = parseNum(stock['التغير %']);
                const isPos = rawPct >= 0;
                const pctStr = `${isPos ? '+' : ''}${rawPct.toFixed(2)}%`;
                const liqVal = parseNum(stock['نسبة السيولة']).toFixed(1);
                const netLiqRaw = parseNum(stock['صافي السيولة']);
                const netLiqM = (netLiqRaw / 1_000_000).toFixed(2);

                return {
                    name: String(stock['الاسم'] || stock['Name'] || t('livePicksUnknownStock')),
                    code: String(stock['الرمز'] || stock['Code'] || '----'),
                    change: String(stock['التغير'] || '0'),
                    changePct: pctStr,
                    price: String(stock['آخر'] || stock['السعر'] || '0.00'),
                    liquidity: `${liqVal}%`,
                    volume: `${netLiqM} م`,
                    isPositive: isPos
                };
            };

            const validStocks = stocks.filter(s => {
                const code = String(s['الرمز'] || s['Code'] || '');
                const name = String(s['الاسم'] || s['Name'] || '');
                if (!code || !name) return false;
                // السوق الرئيسية فقط: 4 أرقام ولا تبدأ بـ 9
                if (!/^\d{4}$/.test(code) || code.startsWith('9')) return false;
                // Exclude stocks with no volume (not actively trading today)
                const vol = String(s['الحجم'] || '0').replace(/,/g, '').trim();
                if (vol === '0' || vol === '-' || vol === '') return false;
                // Exclude 100% liquidity (usually tiny stocks with 1 trade)
                const liqStr = String(s['نسبة السيولة'] || '').replace(/%/g, '').trim();
                const liq = parseFloat(liqStr);
                if (isNaN(liq) || liq >= 99.9) return false;
                return true;
            });

            if (validStocks.length > 0) {
                const gainers = [...validStocks]
                    .filter(s => parseNum(s['التغير %']) > 0)
                    .sort((a, b) => parseNum(b['التغير %']) - parseNum(a['التغير %']))
                    .slice(0, 10)
                    .map(formatStock);

                // Sort liquidity by صافي السيولة (net SAR) descending — biggest actual money flow
                const liquidities = [...validStocks]
                    .filter(s => parseNum(s['صافي السيولة']) > 0)
                    .sort((a, b) => parseNum(b['صافي السيولة']) - parseNum(a['صافي السيولة']))
                    .slice(0, 10)
                    .map(formatStock);

                if (gainers.length > 0) setTopGainers(gainers);
                if (liquidities.length > 0) setTopLiquidity(liquidities);
            }
            const at = riyadhTime(meta.sourceAt);
            if (at) setStamp(at);
            setMode(meta.mode);
        } catch (error) {
            console.error('Error fetching market stats:', error);
        }
    };

    useEffect(() => {
        fetchMarketData();
        const timer = setInterval(() => {
            if (isSaudiMarketOpen() && !document.hidden) fetchMarketData();
        }, 60000);
        const onVisible = () => {
            if (!document.hidden && isSaudiMarketOpen()) fetchMarketData();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    return (
        <section id="market-stats" className="pb-16 pt-8 bg-[#0A1021] relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-purple-900/5 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-900/5 rounded-full blur-3xl pointer-events-none"></div>

            <div className="max-w-6xl mx-auto px-4 sm:px-8 xl:px-0 relative z-10">
                <div className="text-center mb-12">
                    <h2 className="ai-gradient-text text-3xl font-extrabold sm:text-4xl mb-4">
                        {t('marketStatsTitle')}
                    </h2>
                    <p className="max-w-2xl mx-auto text-gray-400 font-medium">
                        {t('marketStatsDesc')}
                    </p>
                    <div className="flex items-center justify-center gap-2 flex-wrap text-xs text-gray-400 mt-4">
                        <Dchip />
                        <span>
                            {mode === 'close'
                                ? t('feedClose', { time: stamp })
                                : t('feedDelayed', { time: stamp })}
                        </span>
                        <span className="text-amber-300/90">· {t('feedSubscribersOnly')}</span>
                        <a href="/market/" className="text-purple-300 hover:text-purple-200 font-bold">جدول كل الأسهم ←</a>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Top 10 Gainers */}
                    <div className="glass-card spotlight rounded-2xl p-6">
                        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/5">
                            <span className="text-2xl">🚀</span>
                            <h3 className="text-2xl font-bold text-white">{t('marketStatsGainers')}</h3>
                        </div>
                        <div className="space-y-3">
                            {!topGainers.length && <p className="text-gray-400 text-sm py-4">جارٍ تحميل بيانات السوق — الأرقام تظهر أثناء الجلسة.</p>}
                            {topGainers.map((stock, i) => (
                                <a key={i} href={`/stock/${encodeURIComponent(stock.code)}/`} className="flex justify-between items-center p-3 rounded-xl hover:bg-white/5 transition-colors border border-transparent hover:border-white/10">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-shrink-0 text-gray-500 font-extrabold text-lg w-6 text-center">{i + 1}</div>
                                        <div>
                                            <div className="font-bold text-white flex items-center gap-2">
                                                <span>{stock.name}</span>
                                                <span className="text-xs text-gray-400 font-normal">({stock.code})</span>
                                                <Dchip />
                                            </div>
                                            <div className="text-xs text-gray-400">{t('price')}: <span className="text-white font-mono">{stock.price}</span></div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="bg-green-500/20 text-green-400 font-bold px-2.5 py-1 rounded-lg text-sm font-mono inline-block">
                                            {stock.changePct}
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Top 10 Liquidity */}
                    <div className="glass-card spotlight rounded-2xl p-6">
                        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/5">
                            <span className="text-2xl">🐋</span>
                            <h3 className="text-2xl font-bold text-white">{t('marketStatsLiquidity')}</h3>
                        </div>
                        <div className="space-y-3">
                            {!topLiquidity.length && <p className="text-gray-400 text-sm py-4">جارٍ تحميل بيانات السوق — الأرقام تظهر أثناء الجلسة.</p>}
                            {topLiquidity.map((stock, i) => (
                                <a key={i} href={`/stock/${encodeURIComponent(stock.code)}/`} className="flex justify-between items-center p-3 rounded-xl hover:bg-white/5 transition-colors border border-transparent hover:border-white/10">
                                    <div className="flex items-center gap-4">
                                        <div className="flex-shrink-0 text-gray-500 font-extrabold text-lg w-6 text-center">{i + 1}</div>
                                        <div>
                                            <div className="font-bold text-white flex items-center gap-2">
                                                <span>{stock.name}</span>
                                                <span className="text-xs text-gray-400 font-normal">({stock.code})</span>
                                                <Dchip />
                                            </div>
                                            <div className="text-xs text-gray-400">نسبة: <span className="text-purple-300 font-mono">{stock.liquidity}</span></div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="bg-purple-500/20 text-purple-400 font-bold px-2.5 py-1 rounded-lg text-sm font-mono inline-block">
                                            +{stock.volume}
                                        </div>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TopTenMarketStats;
